import { DeclarativeShadowElement } from '@theme/critical';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Restores the declarative action system and required refs checks from the
 * original implementation, while keeping ref updates batched behind a shared
 * observer to avoid excessive mutation work during infinite scroll rendering.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
export class Component extends DeclarativeShadowElement {
  /**
   * Shared observer used by all components so we do not create one observer per
   * instance during large collection renders.
   *
   * @type {MutationObserver | null}
   */
  static globalObserver = null;

  /**
   * Roots currently being observed.
   *
   * @type {Map<Node, Component>}
   */
  static observedRoots = new Map();

  /**
   * @returns {MutationObserver}
   */
  static getGlobalObserver() {
    if (Component.globalObserver) return Component.globalObserver;

    Component.globalObserver = new MutationObserver((mutations) => {
      const affectedComponents = new Set();

      for (const mutation of mutations) {
        const component = getClosestComponent(mutation.target);
        if (component instanceof Component) {
          affectedComponents.add(component);
        }
      }

      affectedComponents.forEach((component) => component.scheduleUpdateRefs());
    });

    return Component.globalObserver;
  }

  /**
   * Reconnect the shared observer with the current list of component roots.
   */
  static refreshObservedRoots() {
    const observer = Component.getGlobalObserver();
    observer.disconnect();

    for (const root of Component.observedRoots.keys()) {
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['ref'],
        attributeOldValue: true,
      });
    }
  }

  /**
   * Register a component's light DOM and shadow root for observation.
   *
   * @param {Component} component
   */
  static observeComponent(component) {
    for (const root of component.roots) {
      Component.observedRoots.set(root, component);
    }

    Component.refreshObservedRoots();
  }

  /**
   * Remove all observed roots owned by a component.
   *
   * @param {Component} component
   */
  static unobserveComponent(component) {
    for (const [root, owner] of Component.observedRoots.entries()) {
      if (owner === component) {
        Component.observedRoots.delete(root);
      }
    }

    Component.refreshObservedRoots();
  }

  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Whether a refs refresh is already queued.
   *
   * @type {boolean}
   */
  isUpdateRefsPending = false;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.classList.add('initialized');
    this.updateRefs();
    this.scheduleUpdateRefs();

    requestIdleCallback(() => {
      if (!this.isConnected) return;

      Component.observeComponent(this);
    });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.scheduleUpdateRefs();
  }

  disconnectedCallback() {
    Component.unobserveComponent(this);
  }

  /**
   * Batch refs work into the next frame to avoid hot mutation loops while
   * collection pages are appended/pruned.
   */
  scheduleUpdateRefs() {
    if (this.isUpdateRefsPending) return;

    this.isUpdateRefsPending = true;

    requestAnimationFrame(() => {
      this.isUpdateRefsPending = false;
      if (!this.isConnected) return;

      this.updateRefs();

      if (typeof this.refsUpdated === 'function') {
        this.refsUpdated();
      }
    });
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref`
   * attributes and storing references to them.
   */
  updateRefs() {
    const refs = /** @type {any} */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node
   * @returns {boolean}
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;

  refsUpdated() {}
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node
 * @returns {Node | null}
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node
 * @returns {HTMLElement | null}
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback !== 'function') return;

        try {
          /** @type {(Event | Data)[]} */
          const args = [proxiedEvent];

          if (data) args.unshift(parseData(data));

          callback.call(instance, ...args);
        } catch (error) {
          console.error(error);
        }
      },
      { capture: true }
    );
  }

  /**
   * @param {Event} event
   * @returns {Element | undefined | null}
   */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str
 * @returns {Object | Array<string | number> | string}
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object | Array<string | number> | string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str
 * @returns {Data}
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!Number.isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}

export default Component;
